import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, CalendarCheck, Send, Clock, BadgePercent } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";

const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
const short = (n) => (n >= 100000 ? `${(n / 100000).toFixed(1)}L` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);

const Tip = ({ active, payload, label, k }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-700">{fmtDay(label)}</div>
      <div className="mt-1 font-display text-base font-bold text-[#0A1F44]">{inr(payload[0].value)}</div>
      <div className="text-slate-400">{payload[0].payload[`${k}_count`]} txn</div>
    </div>
  ) : null;

const Stat = ({ icon: I, label, value, sub, tone, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-500">{label}<I className={`h-4 w-4 ${tone}`} /></div>
    <div className="mt-2 font-display text-xl font-bold text-[#0A1F44] sm:text-2xl">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
  </div>
);

const ChartCard = ({ title, total, color, children, testid }) => (
  <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm font-semibold text-slate-600">{title}</div>
        <div className="font-display text-2xl font-bold" style={{ color }}>{total}</div>
      </div>
    </div>
    <div className="mt-4 h-56">{children}</div>
  </div>
);

const axis = { tick: { fontSize: 11, fill: "#94A3B8" }, axisLine: false, tickLine: false };

export const DashboardCharts = () => {
  const { tick } = useAuth();
  const [days, setDays] = useState(7);
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/me/stats", { params: { days } }).then((r) => setS(r.data)); }, [days, tick]);

  const series = s?.series || [];
  const rangePayin = series.reduce((a, x) => a + x.payin, 0);
  const rangePayout = series.reduce((a, x) => a + x.payout, 0);

  return (
    <div className="space-y-4" data-testid="dashboard-charts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-[#0A1F44]">Statistics</h2>
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`chart-range-${d}`}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${days === d ? "bg-[#0A1F44] text-white" : "text-slate-500 hover:text-slate-800"}`}>
              {d}D
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat testid="stat-total-payin" icon={TrendingUp} tone="text-emerald-600" label="Total Pay-in" value={inr(s?.total_payin)} sub={`${s?.payin_count ?? 0} payments`} />
        <Stat testid="stat-today-payin" icon={CalendarCheck} tone="text-emerald-600" label="Today Pay-in" value={inr(s?.today_payin)} sub={`${s?.today_payin_count ?? 0} payments`} />
        <Stat testid="stat-total-payout" icon={Send} tone="text-blue-600" label="Total Payout" value={inr(s?.total_payout)} sub={`${s?.payout_count ?? 0} approved`} />
        <Stat testid="stat-pending-payout" icon={Clock} tone="text-amber-500" label="Pending Payout" value={inr(s?.pending_payout)} />
        <Stat testid="stat-success-rate" icon={BadgePercent} tone="text-blue-600" label="Payout Success" value={s?.success_rate == null ? "-" : `${s.success_rate}%`} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard testid="payin-chart" title={`Pay-in · last ${days} days`} total={inr(rangePayin)} color="#059669">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ left: -12, right: 4, top: 4 }}>
              <defs><linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} {...axis} minTickGap={16} />
              <YAxis tickFormatter={short} {...axis} />
              <Tooltip content={<Tip k="payin" />} />
              <Area type="monotone" dataKey="payin" stroke="#059669" strokeWidth={2.5} fill="url(#gIn)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard testid="payout-chart" title={`Payout · last ${days} days`} total={inr(rangePayout)} color="#2563EB">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ left: -12, right: 4, top: 4 }}>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} {...axis} minTickGap={16} />
              <YAxis tickFormatter={short} {...axis} />
              <Tooltip content={<Tip k="payout" />} cursor={{ fill: "#EFF6FF" }} />
              <Bar dataKey="payout" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};
